import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Users, Loader2, Building2, BadgePercent, Mail, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { submitTeamEnrollment, getTeamEnrollmentPricing } from '@/lib/api';

function useQueryParam(name) {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get(name), [search, name]);
}

export default function TeamEnrollmentPage() {
  const preselectedCert = useQueryParam('cert') || '';

  const [pricing, setPricing] = useState({ price_per_seat: 29, min_seats: 3, volume_discounts: [], certs: [] });
  const [form, setForm] = useState({
    cert_code: preselectedCert,
    org_name: '',
    contact_name: '',
    contact_email: '',
    seats: 10,
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {estimated_total, cert_label, message}

  useEffect(() => {
    getTeamEnrollmentPricing()
      .then(({ data }) => {
        setPricing(data);
        // Ensure cert_code default if preselected is invalid
        if (!preselectedCert && data.certs?.length) {
          setForm((prev) => ({ ...prev, cert_code: data.certs[0].code }));
        }
      })
      .catch(() => {});
  }, [preselectedCert]);

  const selectedCert = (pricing.certs || []).find((c) => c.code === form.cert_code);
  const effectivePricePerSeat = selectedCert?.price_per_seat ?? pricing.price_per_seat;
  const subtotal = effectivePricePerSeat * form.seats;
  const activeDiscount = [...(pricing.volume_discounts || [])].reverse().find((d) => form.seats >= d.threshold);
  const discountAmount = activeDiscount ? (subtotal * activeDiscount.discount_pct) / 100 : 0;
  const total = subtotal - discountAmount;

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const setNum = (k, min = 3) => (e) => setForm((prev) => ({ ...prev, [k]: Math.max(min, parseInt(e.target.value || '0', 10) || min) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cert_code || !form.org_name || !form.contact_name || !form.contact_email) {
      toast.error('Please fill in the required fields');
      return;
    }
    if (form.seats < 3) {
      toast.error(`Minimum team size is ${pricing.min_seats} seats`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await submitTeamEnrollment({
        cert_code: form.cert_code,
        org_name: form.org_name.trim(),
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        seats: form.seats,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      });
      toast.success(data.message);
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg || 'Invalid input' : (detail || 'Could not submit. Try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-emerald-50 via-white to-white" data-testid="team-enrollment-success">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-emerald-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-3xl">You&apos;re in — sales will reach out</CardTitle>
              <CardDescription className="text-base mt-2">{submitted.message}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between text-sm mb-2"><span className="text-slate-500">Track</span><span className="font-medium text-slate-900">{submitted.cert_label}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Estimated total</span><span className="font-semibold text-slate-900">${submitted.estimated_total.toLocaleString()}/mo</span></div>
              </div>
              <p className="text-sm text-slate-600 text-center">
                A Stripe invoice with your final quote will arrive within 1 business day. Volume discounts (25 / 50 / 100+ seats) applied automatically.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-br from-indigo-50 via-white to-white" data-testid="team-enrollment-page">
      {/* HERO */}
      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge variant="outline" className="mb-3 border-indigo-300 text-indigo-700">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Team enrollment · from ${pricing.price_per_seat}/user/month
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 max-w-3xl">
            Enroll your team — one invoice, one dashboard, unlimited progress.
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            All Microsoft, Google, and NVIDIA certs. Team admin dashboard, per-learner progress reports, and bulk discounts starting at 25 seats.
          </p>
        </div>
      </section>

      {/* FORM + SUMMARY */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* LEFT: form */}
            <div className="lg:col-span-2">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Tell us about your team</CardTitle>
                  <CardDescription>Everything below stays with us. A specialist will follow up with a Stripe invoice.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="team-enrollment-form">
                    <div>
                      <Label htmlFor="cert_code">Which cert(s) is this for? *</Label>
                      <select
                        id="cert_code"
                        value={form.cert_code}
                        onChange={set('cert_code')}
                        required
                        className="mt-1 h-10 rounded-md border border-slate-300 bg-white text-slate-900 px-3 text-sm w-full"
                        data-testid="team-enrollment-cert-select"
                      >
                        <option value="">Select a certification</option>
                        {(pricing.certs || []).map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="org_name">Organization *</Label>
                        <Input id="org_name" value={form.org_name} onChange={set('org_name')} required data-testid="team-enrollment-org-input" />
                      </div>
                      <div>
                        <Label htmlFor="seats">Number of learners *</Label>
                        <Input id="seats" type="number" min={pricing.min_seats} value={form.seats} onChange={setNum('seats', pricing.min_seats)} required data-testid="team-enrollment-seats-input" />
                        <p className="text-xs text-slate-500 mt-1">Minimum {pricing.min_seats}</p>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact_name">Your name *</Label>
                        <Input id="contact_name" value={form.contact_name} onChange={set('contact_name')} required data-testid="team-enrollment-name-input" />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Work email *</Label>
                        <Input id="contact_email" type="email" value={form.contact_email} onChange={set('contact_email')} required data-testid="team-enrollment-email-input" />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input id="phone" value={form.phone} onChange={set('phone')} data-testid="team-enrollment-phone-input" />
                    </div>

                    <div>
                      <Label htmlFor="message">Anything else? (optional)</Label>
                      <Textarea id="message" rows={3} value={form.message} onChange={set('message')} placeholder="Timelines, mixed cert paths, custom onboarding needs, etc." data-testid="team-enrollment-message-input" />
                    </div>

                    <Button type="submit" size="lg" disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="team-enrollment-submit-btn">
                      {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>) : (<>Request team quote <ArrowRight className="w-4 h-4 ml-2" /></>)}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      We&apos;ll follow up within 1 business day. No card required at this step.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* RIGHT: summary */}
            <div className="lg:col-span-1">
              <Card className="border-indigo-200 sticky top-24" data-testid="team-enrollment-summary">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-600" /> Order preview</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Base ({form.seats} × ${effectivePricePerSeat})</span>
                    <span className="font-medium text-slate-900">${subtotal.toLocaleString()}</span>
                  </div>
                  {activeDiscount && (
                    <div className="flex justify-between text-emerald-700" data-testid="team-enrollment-discount-row">
                      <span className="flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> {activeDiscount.label}</span>
                      <span className="font-medium">−${discountAmount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex justify-between text-base">
                    <span className="text-slate-700 font-semibold">Estimated total</span>
                    <span className="font-bold text-slate-900" data-testid="team-enrollment-total">${total.toLocaleString()}/mo</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Billed by Stripe invoice — you&apos;ll only be charged after you approve the final quote.
                  </p>
                  <div className="bg-indigo-50 rounded-lg p-3 space-y-2 text-xs text-indigo-900">
                    <p className="font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> What&apos;s included</p>
                    <p>Team admin dashboard · Per-learner progress · Bulk seat provisioning · AI Coach 1:1 · All practice modes</p>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 pt-1">
                    <Mail className="w-3 h-3" /> Questions? <a href="mailto:info@learnquanta.com" className="text-indigo-600 hover:underline">info@learnquanta.com</a>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
